import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { API } from '../utils/api'
import { Api } from '../utils/apiServer'
import { session } from '../utils/AuthSession'

const GUEST_STORE_KEY = 'loyalty_guest_store_slug'
export const StoreContext = createContext(null)

const storesFromResponse = (response) => {
    const value = response?.stores || response?.data?.data || response?.data
    return Array.isArray(value) ? value : []
}

export const StoreProvider = ({ children }) => {
    const [stores, setStores] = useState([])
    const [activeStoreSlug, setActiveStoreSlug] = useState('')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const refreshPromiseRef = useRef(null)

    const refresh = useCallback(async () => {
        if (refreshPromiseRef.current) return refreshPromiseRef.current

        setLoading(true)
        setError('')
        const request = (async () => {
            try {
            const response = await Api({ access: 'public', method: API.PUBLIC_STORE.type, url: API.PUBLIC_STORE.url, reqBody: {} })
            const availableStores = storesFromResponse(response)
            if (!response?.status || !availableStores.length) {
                setStores([])
                setActiveStoreSlug('')
                setError(response?.message || 'Daftar toko belum tersedia.')
                return
            }
            const currentSession = session()
            const fallbackSlug = response?.default_store_slug || ''
            const preferredSlug = currentSession?.default_store_slug || currentSession?.user?.default_store_slug || localStorage.getItem(GUEST_STORE_KEY) || fallbackSlug
            const selected = availableStores.find((store) => `${store?.store_slug || store?.slug || ''}` === `${preferredSlug}`)
            const fallback = availableStores.find((store) => `${store?.store_slug || store?.slug || ''}` === `${fallbackSlug}`)
            const nextSlug = selected
                ? preferredSlug
                : (fallback?.store_slug || fallback?.slug || availableStores[0]?.store_slug || availableStores[0]?.slug || '')
            setStores(availableStores)
            setActiveStoreSlug(nextSlug)
            if (!currentSession && nextSlug) localStorage.setItem(GUEST_STORE_KEY, nextSlug)
            } catch (requestError) {
                setStores([])
                setActiveStoreSlug('')
                setError('Gagal memuat daftar toko.')
            } finally {
                setLoading(false)
            }
        })()

        refreshPromiseRef.current = request
        try {
            return await request
        } finally {
            if (refreshPromiseRef.current === request) refreshPromiseRef.current = null
        }
    }, [])

    useEffect(() => {
        refresh()
        window.addEventListener('loyalty-session-changed', refresh)
        return () => window.removeEventListener('loyalty-session-changed', refresh)
    }, [refresh])

    const selectStore = useCallback(async (storeSlug) => {
        const store = stores.find((item) => `${item?.store_slug || item?.slug || ''}` === `${storeSlug}`)
        if (!store) return { status: false, message: 'Toko tidak ditemukan' }
        if (session()) {
            const response = await Api({ access: 'auth', method: API.DEFAULT_STORE.type, url: API.DEFAULT_STORE.url, reqBody: { store_slug: storeSlug } })
            if (!response?.status) return response
        } else {
            localStorage.setItem(GUEST_STORE_KEY, storeSlug)
        }
        setActiveStoreSlug(storeSlug)
        return { status: true, data: store }
    }, [stores])

    const activeStore = useMemo(() => stores.find((store) => `${store?.store_slug || store?.slug || ''}` === `${activeStoreSlug}`) || null, [stores, activeStoreSlug])
    return <StoreContext.Provider value={{ stores, activeStore, activeStoreSlug, loading, error, selectStore, refresh }}>{children}</StoreContext.Provider>
}
